/**
 * Значки сайдбара и общие значки кабинета.
 *
 * Свои SVG прямо в коде, а не пакет иконок и тем более не шрифт с чужого
 * домена: при шатдауне с белым списком страница обязана отрисоваться целиком
 * (раздел 4 CLAUDE.md). Те же правила, что у значков материалов: viewBox
 * 24x24, `currentColor`, ни одного размера внутри. Размер и цвет задаёт CSS,
 * поэтому один и тот же значок читается и в развёрнутом сайдбаре, и в узкой
 * полосе с одними иконками.
 *
 * ⚠️ Значок пустой для читалок (`aria-hidden`), и подпись живёт не в нём. У
 * пункта сайдбара она стоит рядом словом, у кнопки-значка на самой кнопке
 * (`aria-label` плюс `title`). То же правило и теми же словами записано у
 * значков карточки занятия (`features/schedule/icons.tsx`).
 *
 * ⚠️ **Исключение из него ровно одно, и объявляется оно доводом `label`.**
 * Правило выше держится на том, что слово стоит РЯДОМ. Там, где значок заменил
 * собой слово (раздел 8б `docs/cabinet-tables-design.md`), рядом нет ничего, и
 * молчащий значок отнял бы смысл у читалки целиком: строка расписания перестала
 * бы отличать личное занятие от группового. Такой значок получает `role="img"`
 * и подпись элементом `<title>` вместо `aria-hidden`.
 *
 * Довод необязательный намеренно, и умолчание это молчание: забыть можно только
 * подпись, а не запрет, поэтому новый значок по недосмотру заговорить не может.
 */

function Svg(props: {
  children: React.ReactNode;
  className?: string | undefined;
  label?: string | undefined;
}) {
  return (
    <svg
      /*
        Класс приходит снаружи, потому что размер у значка не один. `side__icon`
        это 1.25rem пункта сайдбара, `kind__ico` (вид занятия в расписании) задан
        в `em`, потому что тот значок стоит внутри текста и обязан быть по росту
        строке, в которой оказался.

        ⚠️ Размера кнопки-значка тут больше НЕТ, и это не забывчивость: с
        14.09.2026 фигуру внутри кнопки меряет сама кнопка (`.btn svg`, ступень
        `--sz-icon`). Значку, который стоит в кнопке, класс не нужен вовсе.
      */
      className={props.className ?? "side__icon"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      /*
        Без `label` значок идёт рядом с подписью (в узкой полосе она остаётся в
        разметке для читалок), поэтому для доступности он пустой. С `label` он
        сам и есть подпись, потому что слова рядом нет: см. шапку файла
      */
      aria-hidden={props.label === undefined ? true : undefined}
      role={props.label === undefined ? undefined : "img"}
    >
      {/*
        ⚠️ Подпись это `<title>` внутри значка, а не `aria-label` рядом с ним, и
        источник у неё ровно ОДИН. Элемент делает сразу две вещи: даёт читалке
        имя фигуры и всплывает подсказкой под мышью. Подсказка тут не украшение:
        у кнопки-значка её даёт атрибут `title`, но на SVG браузеры этот атрибут
        не показывают, а зрячему человеку, не опознавшему фигуру, узнать её
        название иначе нечем. Пара «`aria-label` плюс `<title>`» положила бы
        одну и ту же строку в разметку дважды, и часть читалок прочитала бы её
        дважды тоже.
      */}
      {props.label !== undefined && <title>{props.label}</title>}
      {props.children}
    </svg>
  );
}

/**
 * Расписание: календарная сетка.
 *
 * Не часы: часы это «когда сейчас», а раздел про повторяющиеся дни недели.
 * Отличается от экрана на подставке (уроки) перемычкой сверху и клетками,
 * то есть с первого взгляда и в узкой полосе, где от пункта остаётся значок.
 */
export function ScheduleIcon() {
  return (
    <Svg>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <path d="M8 14h.01M12 14h.01M16 14h.01M8 17.5h.01M12 17.5h.01" />
    </Svg>
  );
}

/**
 * Уроки: экран на подставке, то есть занятие у доски.
 *
 * ⚠️ Эта фигура раньше означала заготовки. Их значок стал стопкой листов, и
 * это не перестановка ради красоты: заготовка это материал, который копируют,
 * а урок это занятие. Одинаковые фигуры у соседних разделов сайдбара человек
 * различать не обязан.
 */
export function LessonsIcon() {
  return (
    <Svg>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M12 16v4M8 20h8" />
    </Svg>
  );
}

/** Ученики: двое. Пара «преподаватель и ученик» это связь, а не один человек. */
export function StudentsIcon() {
  return (
    <Svg>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.5a3.2 3.2 0 0 1 0 6.2" />
      <path d="M17.5 14.5A5.5 5.5 0 0 1 20.5 19.5" />
    </Svg>
  );
}

/**
 * Один человек: личное занятие в строке расписания, в пару к фигуре группы.
 *
 * ⚠️ Не `StudentsIcon` соседней строкой выше, и это не вкусовщина. Тот значок
 * полтора человека и означает РАЗДЕЛ «Ученики»; рядом с `GroupsIcon` он читался
 * бы как «группа поменьше», а весь вопрос здесь ровно в том, один человек или
 * несколько. Фигура по центру и одна, чтобы разница ловилась взглядом в коробке
 * занятия шириной в полколонки.
 *
 * Доводы те же, что у `GroupsIcon`: подпись для читалки стоит на самом значке
 * (`label`), потому что слова рядом с ним нет.
 */
export function PersonIcon(props: { className?: string | undefined; label?: string | undefined }) {
  return (
    <Svg className={props.className} label={props.label}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    </Svg>
  );
}

/**
 * Позвать коллегу: один человек с плюсом (`docs/colleague-referral-design.md`,
 * 6.1).
 *
 * ⚠️ Не `StudentsIcon` и не `PersonIcon` без плюса: пункт стоит в том же меню,
 * что «Ученики», и похожая фигура читалась бы вторым входом туда же. Плюс
 * говорит «добавить человека», а фигура сдвинута влево, чтобы ему было место.
 */
export function InviteIcon() {
  return (
    <Svg>
      <circle cx="9.5" cy="8" r="3.5" />
      <path d="M3 20a6.5 6.5 0 0 1 13 0" />
      <path d="M19 8v6M16 11h6" />
    </Svg>
  );
}

/** Заготовки: стопка листов. Материал, с которого снимают копию. */
export function PlansIcon() {
  return (
    <Svg>
      <rect x="8" y="3" width="12" height="14" rx="2" />
      <path d="M16 20.5H6a2 2 0 0 1-2-2V7" />
    </Svg>
  );
}

/** Мои предметы: раскрытая книга, две страницы от корешка. */
export function SubjectsIcon() {
  return (
    <Svg>
      <path d="M12 6.5C10.5 5 8 4.5 4 4.5v13c4 0 6.5.5 8 2" />
      <path d="M12 6.5c1.5-1.5 4-2 8-2v13c-4 0-6.5.5-8 2z" />
    </Svg>
  );
}

/** Материалы: папка, как и в самом проводнике материалов. */
export function MediaIcon() {
  return (
    <Svg>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </Svg>
  );
}

/**
 * Карта тем: три узла и связи, то есть буквально граф.
 *
 * Не карта местности и не компас: раздел показывает зависимость тем друг от
 * друга, и значок должен читаться как «связанные точки», а не «путешествие».
 */
export function CoverageIcon() {
  return (
    <Svg>
      <circle cx="5.5" cy="6" r="2.5" />
      <circle cx="5.5" cy="18" r="2.5" />
      <circle cx="18" cy="12" r="2.5" />
      <path d="M7.8 7.1l7.9 3.8M7.8 16.9l7.9-3.8" />
    </Svg>
  );
}

/**
 * Домашка: тетрадный лист с отметкой выполнения.
 *
 * Не портфель и не домик: домик занят метафорой «домой», а портфель читается
 * как «работа». Лист с галочкой это «задание, которое проверяют», и он
 * отличается и от стопки листов (заготовки), и от коробки (архив) в узкой
 * полосе, где от пункта остаётся один значок.
 */
export function HomeworkIcon() {
  return (
    <Svg>
      <path d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
      <path d="M9 3v18" />
      <path d="M12.5 12l1.8 1.8 3.2-3.6" />
    </Svg>
  );
}

/**
 * Архив: коробка с крышкой и хватом.
 *
 * Не часы и не стрелка назад: и то и другое читается как «история действий» или
 * «отменить», а здесь лежат проведённые занятия. Коробка отличается от стопки
 * листов (заготовки) и от экрана на подставке (уроки) с первого взгляда, что и
 * требуется в узкой полосе, где от пункта остаётся один значок.
 */
export function ArchiveIcon() {
  return (
    <Svg>
      <rect x="3" y="4" width="18" height="4.5" rx="1" />
      <path d="M5 8.5V18a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.5" />
      <path d="M10 12h4" />
    </Svg>
  );
}

/** Выход: дверь со стрелкой наружу. */
/**
 * Ключ: смена пароля.
 *
 * Именно ключ, а не замок: замком в интерфейсах обозначают запрет или закрытый
 * доступ, и в ряду с «Выйти» он читался бы как «заблокировать». Ключ читается
 * как «то, чем открывают», то есть как сам пароль.
 */
export function KeyIcon() {
  return (
    <Svg>
      <circle cx="8" cy="15" r="4" />
      <path d="M10.9 12.1 20 3M17 6l2.5 2.5M14.5 8.5 17 11" />
    </Svg>
  );
}

/**
 * Подпись на доске: ярлык с петелькой, то есть бейдж с именем.
 *
 * Не карандаш намеренно. Карандаш на этом экране уже означает рисование (доска,
 * заготовки), и пункт «как меня подписывать» с карандашом читался бы как «взять
 * инструмент». Ярлык говорит «имя, которое на мне висит», и это ровно правда.
 */
export function NameTagIcon() {
  return (
    <Svg>
      <path d="M4 9h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1z" />
      <path d="M9 9V5a3 3 0 0 1 6 0v4" />
      <path d="M7.5 14h9M7.5 17h5" />
    </Svg>
  );
}

/**
 * Светлая или тёмная: круг, залитый наполовину.
 *
 * Не солнце и не месяц намеренно. Каждая из этих двух фигур называет ОДНО из
 * трёх состояний, то есть пункт меню выглядел бы уже нажатым, а третье
 * состояние («как в системе») не называет ни одна. Половина круга говорит про
 * саму пару, а не про выбранное из неё.
 *
 * Заливка здесь единственная во всём файле: остальные значки нарисованы одним
 * штрихом. Без неё половина не читается вовсе, а `currentColor` держит и её,
 * поэтому цвет по-прежнему задаёт CSS.
 */
export function ThemeIcon() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 3.5a8.5 8.5 0 0 1 0 17z" fill="currentColor" stroke="none" />
    </Svg>
  );
}

export function SignOutIcon() {
  return (
    <Svg>
      <path d="M10 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4" />
      <path d="M16 16l4-4-4-4M20 12H10" />
    </Svg>
  );
}

/**
 * Что нового: искра.
 *
 * Не колокол намеренно. Колокол это уведомление, то есть обещание, что нас
 * дёрнут; продукт такого обещания не даёт вовсе (раздел 4 CLAUDE.md запрещает
 * чужие мессенджеры как транспорт, а своего у нас нет). Искра говорит
 * «появилось новое», а не «вас зовут», и это ровно то, что здесь правда.
 */
export function SparkIcon() {
  return (
    <Svg>
      <path d="M12 3.5l1.9 4.6 4.6 1.9-4.6 1.9L12 16.5l-1.9-4.6L5.5 10l4.6-1.9z" />
      <path d="M18.5 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </Svg>
  );
}

/**
 * Сочинить: палочка с искрой.
 *
 * ⚠️ Не `SparkIcon`, хотя искра это привычный значок для ИИ. Искра в продукте
 * уже занята и означает «появилось новое» (пункт «Что нового» в сайдбаре), а
 * один значок на два разных смысла на соседних экранах превращает оба в шум.
 * Палочка говорит «сделает за вас», и это ровно то, что здесь правда.
 *
 * Слово рядом остаётся: значок тут не заменяет подпись, а помечает, что за
 * кнопкой стоит машина, которая пишет текст сама.
 */
export function WandIcon(props: { className?: string | undefined }) {
  return (
    <Svg className={props.className}>
      <path d="M4 20l10.5-10.5" />
      <path d="M14 6.5l3.5 3.5" />
      <path d="M17.5 3.2l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
    </Svg>
  );
}

/**
 * Сообщения: облачко реплики.
 *
 * Не конверт: конверт это письмо, то есть обещание доставки, а транспорта
 * уведомлений у продукта нет (раздел 4 CLAUDE.md). И не колокол, по той же
 * причине, по которой его нет у «Что нового»: колокол говорит «вас зовут».
 *
 * Размер приходит снаружи с 10.09.2026: тот же значок стоит теперь на кнопке
 * чата занятия в доке комнаты, а там кнопка мельче строки сайдбара. Умолчание
 * не поменялось, поэтому у сайдбара всё осталось как было.
 */
export function MessagesIcon(props: { className?: string | undefined }) {
  return (
    <Svg className={props.className}>
      <path d="M20 3H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h2v4l5-4h9a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z" />
      <path d="M7 8h10M7 12h6" />
    </Svg>
  );
}

/**
 * Группы: срез списка учеников, поэтому и значок родственный «Ученикам».
 *
 * Доводы нужны второму месту показа: в строке расписания эта же фигура стоит
 * без слова рядом, то есть подписывает себя сама (`label`), и размер там мельче
 * сайдбарного (`className`). Своей фигуры группы расписание не заводит: два
 * разных значка для одной вещи хуже слова (раздел 8б
 * `docs/cabinet-tables-design.md`).
 */
export function GroupsIcon(props: { className?: string | undefined; label?: string | undefined }) {
  return (
    <Svg className={props.className} label={props.label}>
      <path d="M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3 20a6 6 0 0 1 12 0" />
      <path d="M16 11a3 3 0 1 0 0-6M17 20a6 6 0 0 0-2-4.5" />
    </Svg>
  );
}

/**
 * Ученик записался сам: фигура человека с галочкой. Стоит в коробке занятия
 * расписания рядом с видом занятия и подписывает себя сама (`label`): слова
 * рядом нет, в коробку оно не влезает (`docs/booking-design.md`, 3.2).
 */
export function SelfBookedIcon(props: {
  className?: string | undefined;
  label?: string | undefined;
}) {
  return (
    <Svg className={props.className} label={props.label}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M15.5 11.5l2 2 4-4.5" />
    </Svg>
  );
}

/**
 * Два прямоугольника внахлёст: поверх открытого для записи времени стоит
 * занятие. Метка в коробке окна, а не подпись: в узкую колонку телефона слово
 * не влезает (3.2 проекта), поэтому подписывает себя сама (`label`).
 */
export function OverlapIcon(props: { className?: string | undefined; label?: string | undefined }) {
  return (
    <Svg className={props.className} label={props.label}>
      <rect x="3" y="4" width="11" height="10" rx="1.5" />
      <rect x="10" y="10" width="11" height="10" rx="1.5" />
    </Svg>
  );
}

/**
 * Убрать: корзина. Размер приходит от самой кнопки (`.btn svg`).
 *
 * ⚠️ Именно корзина, и это не спор с соседним файлом, а его продолжение: у
 * значков карточки занятия корзины нет ровно потому, что занятие серии не
 * удаляется, а снимается (`features/schedule/icons.tsx`, `CancelIcon`). Здесь
 * удаление настоящее, и универсальная фигура удаления тут на своём месте.
 *
 * Обещания вернуть она не даёт: корзиной с тридцатью днями в продукте зовётся
 * ровно то, что так называется словами на экране (уроки, домашка), а у
 * сообщения такого экрана нет вовсе. Что удаление окончательное, говорит шаг
 * подтверждения, а не фигура.
 */
export function TrashIcon() {
  return (
    <Svg>
      <path d="M4 6.5h16" />
      <path d="M9.5 6.5V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 14.5 5v1.5" />
      <path d="M6.5 6.5 7.3 19a2 2 0 0 0 2 1.9h5.4a2 2 0 0 0 2-1.9l.8-12.5" />
      <path d="M10.3 10.5v6.5M13.7 10.5v6.5" />
    </Svg>
  );
}

/*
  Две фигуры кнопки образца голоса (`features/tts/LinesEditor.tsx`): покой и
  звучание. Стоят парой, потому что читает их одна кнопка и разойтись им нельзя.

  ⚠️ Значок вместо слова здесь законен ровно потому, что фигура универсальна:
  треугольник это «играть» на любом плеере, квадрат это «стоп». Подпись у обеих
  живёт на кнопке (`aria-label` плюс `title`), как у остальных значков кабинета.
*/

/**
 * Играть: треугольник. С заливкой, потому что контурный треугольник это
 * указатель, а не кнопка воспроизведения.
 */
export function PlayIcon() {
  return (
    <Svg>
      <path d="M8 5.5 18.5 12 8 18.5z" fill="currentColor" />
    </Svg>
  );
}

/**
 * Стоп: квадрат. Не пауза с двумя полосками: образец не продолжают с того же
 * места, его слушают заново.
 */
export function StopIcon() {
  return (
    <Svg>
      <rect x="6.5" y="6.5" width="11" height="11" rx="1.6" fill="currentColor" />
    </Svg>
  );
}

/*
  Три фигуры кнопки копирования (`shell/CopyLink.tsx`): покой, успех и отказ.
  Стоят тройкой, потому что читает их одна кнопка и разойтись им нельзя.
*/

/**
 * Копировать: планшет с зажимом, то есть буфер обмена.
 *
 * ⚠️ **Не два наложенных листа, хотя это самая частая фигура копирования.**
 * Стопка листов в кабинете занята и означает «Заготовки» (`PlansIcon` выше,
 * дословно «материал, с которого снимают копию»), а два смысла у одной фигуры
 * хуже слова (правило 1 раздела 8б `docs/cabinet-tables-design.md`). Планшет
 * говорит про место, куда адрес кладут, и в кабинете ничем не занят: от ярлыка
 * с петелькой (`NameTagIcon`) он отличается ростом и прямоугольным зажимом, от
 * тетради (`HomeworkIcon`) отсутствием корешка слева.
 */
export function CopyIcon() {
  return (
    <Svg>
      <path d="M15 4.5h2a2 2 0 0 1 2 2V19a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6.5a2 2 0 0 1 2-2h2" />
      <rect x="9" y="2.5" width="6" height="4" rx="1.2" />
    </Svg>
  );
}

/** Скопировано: галочка. Тот же ответ, что слово «Скопировано» у соседнего вида. */
export function CopiedIcon() {
  return (
    <Svg>
      <path d="M20 6.5 9.5 17 4 11.5" />
    </Svg>
  );
}

/**
 * Скопировать не вышло: восклицательный знак в треугольнике.
 *
 * ⚠️ Фигура здесь не заменяет слова, и заменить их не может: отказ объясняют
 * текстом, потому что человеку нужна причина, а не категория (раздел 8б, абзац
 * «Чего правило не разрешает»). Она нужна ровно для того, чтобы кнопка
 * перестала выглядеть так же, как в покое: иначе нажатие выглядит проглоченным.
 */
export function CopyFailedIcon() {
  return (
    <Svg>
      <path d="M12 4.2 21 19.8H3z" />
      <path d="M12 10v4M12 17h.01" />
    </Svg>
  );
}

/** Карточки: две карточки внахлёст, задняя повёрнута (раздел «Карточки» и «Колоды»). */
export function CardsIcon() {
  return (
    <Svg>
      <rect x="7" y="4" width="13" height="16" rx="2" />
      <path d="M4.5 7.5 7 6.8" />
      <path d="M4 9.5v9a2 2 0 0 0 2.4 2l1.6-.4" />
    </Svg>
  );
}

/**
 * Три точки: «Ещё». Читателей два: пункт «Ещё» нижней полосы навигации
 * (`TabBar.tsx`) и свёрнутое меню полосы редактора заготовки на телефоне
 * (`features/plans/PlanBarMore.tsx`). До 19.10.2026 фигура жила в `TabBar.tsx`,
 * потому что читатель был один; второй появился, и место ей здесь.
 *
 * Размер у читателей разный, и задаёт его не значок: в полосе навигации это
 * `.side__icon` по умолчанию, в кнопке `.btn svg`.
 */
export function MoreIcon() {
  return (
    <Svg>
      <circle cx="5" cy="12" r="1.2" />
      <circle cx="12" cy="12" r="1.2" />
      <circle cx="19" cy="12" r="1.2" />
    </Svg>
  );
}
